import { useState, useEffect, useCallback } from "react";
import {
  listTransactions,
  approveTransaction,
  rejectTransaction,
  ApiError,
} from "../services/gridApi";
import { useDemo } from "../context/DemoContext";
import type { TransactionStatus, TransactionType } from "../types";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: Array<TransactionStatus | "ALL"> = [
  "ALL",
  "PENDING",
  "COMPLETED",
  "FAILED",
  "PROCESSING",
];

const TYPE_OPTIONS: Array<TransactionType | "ALL"> = [
  "ALL",
  "INCOMING",
  "OUTGOING",
];

function truncateId(id: string): string {
  if (id.length <= 16) return id;
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(
  amt?: { amount: number; currency: { code: string; symbol: string; decimals: number } | string },
): string {
  if (!amt || amt.amount === 0) return "--";
  // Handle currency as string or object
  const curr = typeof amt.currency === "string"
    ? { code: amt.currency, symbol: "", decimals: 2 }
    : amt.currency;
  const value = amt.amount / Math.pow(10, curr.decimals ?? 0);
  return `${curr.symbol ?? ""}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${curr.code}`;
}

/** Safely stringify a value that might be a string or object for display */
function displayField(val: unknown): string {
  if (val == null) return "--";
  if (typeof val === "string") return val || "--";
  if (typeof val === "object") {
    // Common patterns: { accountId: "..." } or { umaAddress: "..." }
    const obj = val as Record<string, unknown>;
    if (obj.umaAddress) return String(obj.umaAddress);
    if (obj.accountId) return truncateId(String(obj.accountId));
    if (obj.destinationType) return String(obj.destinationType);
    return JSON.stringify(val);
  }
  return String(val);
}

function StatusBadge({ status }: { status: TransactionStatus }) {
  const colorMap: Record<string, string> = {
    COMPLETED: "bg-emerald-500/10 text-emerald-400",
    PENDING: "bg-yellow-500/10 text-yellow-400",
    FAILED: "bg-red-500/10 text-red-400",
    PROCESSING: "bg-blue-500/10 text-blue-400",
    CREATED: "bg-[#00B3E0]/10 text-[#00B3E0]",
    REJECTED: "bg-red-500/10 text-red-400",
    REFUNDED: "bg-orange-500/10 text-orange-400",
    EXPIRED: "bg-[#222222] text-[#666666]",
  };
  const color = colorMap[status] ?? "bg-[#222222] text-[#666666]";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${color}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function TypeBadge({ type }: { type: TransactionType }) {
  const color =
    type === "INCOMING"
      ? "bg-emerald-500/10 text-emerald-400"
      : "bg-orange-500/10 text-orange-400";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-medium ${color}`}
    >
      {type === "INCOMING" ? (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3"
          />
        </svg>
      ) : (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
          />
        </svg>
      )}
      {type.charAt(0) + type.slice(1).toLowerCase()}
    </span>
  );
}

export default function Transactions() {
  const { demo, getDemoTransactions } = useDemo();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | "ALL">("ALL");
  const [typeFilter, setTypeFilter] = useState<TransactionType | "ALL">("ALL");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Demo mode: load from context
  useEffect(() => {
    if (!demo.enabled) return;
    let txs = getDemoTransactions();
    if (statusFilter !== "ALL") txs = txs.filter((t) => t.status === statusFilter);
    if (typeFilter !== "ALL") txs = txs.filter((t) => t.type === typeFilter);
    setTransactions(txs);
    setTotalCount(txs.length);
    setHasMore(false);
    setLoading(false);
    setError(null);
  }, [demo.enabled, getDemoTransactions, statusFilter, typeFilter]);

  const fetchTransactions = useCallback(
    async (nextCursor?: string) => {
      if (demo.enabled) return;
      const isLoadMore = !!nextCursor;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const params: Record<string, unknown> = {
          limit: PAGE_SIZE,
          sortOrder: "desc" as const,
        };
        if (statusFilter !== "ALL") params.status = statusFilter;
        if (typeFilter !== "ALL") params.type = typeFilter;
        if (nextCursor) params.cursor = nextCursor;

        const res = await listTransactions(params as any);

        if (isLoadMore) {
          setTransactions((prev) => [...prev, ...res.data]);
        } else {
          setTransactions(res.data);
        }
        setHasMore(res.hasMore);
        setCursor(res.nextCursor);
        if (res.totalCount !== undefined) setTotalCount(res.totalCount);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `API error ${err.status}: ${err.message}`
            : "Failed to load transactions";
        setError(message);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [statusFilter, typeFilter, demo.enabled],
  );

  useEffect(() => {
    if (demo.enabled) return;
    setCursor(undefined);
    fetchTransactions();
  }, [fetchTransactions, demo.enabled]);

  const handleApprove = async (transactionId: string) => {
    setActionLoading(transactionId);
    try {
      await approveTransaction(transactionId);
      setCursor(undefined);
      await fetchTransactions();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Approve failed (${err.status}): ${err.message}`
          : "Failed to approve transaction";
      setError(message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (transactionId: string) => {
    const reason = window.prompt("Rejection reason (optional):");
    if (reason === null) return; // user cancelled prompt

    setActionLoading(transactionId);
    try {
      await rejectTransaction(transactionId, reason || undefined);
      setCursor(undefined);
      await fetchTransactions();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Reject failed (${err.status}): ${err.message}`
          : "Failed to reject transaction";
      setError(message);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
              Transactions
            </h1>
            {demo.enabled && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
                Demo Mode
              </span>
            )}
          </div>
          <p className="text-sm text-[#888888] mt-1">
            All transactions across your customer base
          </p>
        </div>
        {totalCount !== undefined && (
          <div className="text-sm text-[#888888]">
            {transactions.length} of {totalCount}
          </div>
        )}
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div>
          <label className="block text-xs text-[#888888] mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as TransactionStatus | "ALL")
            }
            className="bg-[#111111] border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2 outline-none focus:border-[#00B3E0]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === "ALL" ? "All Statuses" : s.charAt(0) + s.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-[#888888] mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as TransactionType | "ALL")
            }
            className="bg-[#111111] border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2 outline-none focus:border-[#00B3E0]"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t === "ALL" ? "All Types" : t.charAt(0) + t.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <svg
            className="animate-spin h-8 w-8 text-[#00B3E0] mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-sm text-[#888888]">Loading transactions...</p>
        </div>
      ) : (
        <>
          {/* Transactions Table */}
          <div className="bg-[#111111] border border-[#222222] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#222222]">
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    ID
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Type
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Destination
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Description
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3">
                    Date
                  </th>
                  <th className="text-left text-xs font-medium text-[#888888] px-4 py-3 w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="border-b border-[#222222] last:border-b-0 hover:bg-[#1a1a1a] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-mono text-[#F8F8F7] cursor-help"
                        title={tx.id}
                      >
                        {truncateId(tx.id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <TypeBadge type={tx.type} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-[#F8F8F7]">
                        {formatAmount(tx.sentAmount) !== "--"
                          ? formatAmount(tx.sentAmount)
                          : formatAmount(tx.receivedAmount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs text-[#888888] max-w-[160px] truncate block"
                        title={displayField(tx.destination)}
                      >
                        {displayField(tx.destination)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[#cccccc]">
                        {tx.description || "--"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-[#888888] whitespace-nowrap">
                        {formatDate(tx.createdAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tx.status === "PENDING" && tx.type === "INCOMING" && !demo.enabled && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleApprove(tx.id)}
                            disabled={actionLoading === tx.id}
                            title="Approve transaction"
                            className="p-1.5 rounded text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {actionLoading === tx.id ? (
                              <svg
                                className="animate-spin w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m4.5 12.75 6 6 9-13.5"
                                />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleReject(tx.id)}
                            disabled={actionLoading === tx.id}
                            title="Reject transaction"
                            className="p-1.5 rounded text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-[#888888] py-12"
                    >
                      {demo.enabled
                        ? "No demo transactions yet. Use the Transfer page to create some."
                        : "No transactions found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {hasMore && !demo.enabled && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => fetchTransactions(cursor)}
                disabled={loadingMore}
                className="px-6 py-2.5 rounded-lg bg-[#111111] border border-[#222222] text-sm text-[#F8F8F7] hover:border-[#00B3E0] hover:text-[#00B3E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Loading...
                  </span>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
